import { formatCopy } from '../../../copy/format.js';
import copy from '../../../copy/en/common.json' with { type: 'json' };
/**
 * Form primitives with inline validation. A `Field` owns the label, hint and
 * error; the control receives `aria-invalid` / `aria-describedby` automatically.
 */
import { CircleAlert, Search } from "lucide-react";
import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import { cx } from "./cx.js";
import { HelpPopover } from "./HelpPopover.js";

export interface FieldProps {
  label: ReactNode;
  hint?: ReactNode;
  help?: ReactNode;
  error?: string | null;
  optional?: boolean;
  /** Render prop receives the ids/aria wiring for the control. */
  children: (control: {
    id: string;
    "aria-invalid": true | undefined;
    "aria-describedby": string | undefined;
  }) => ReactNode;
  className?: string;
}

export function Field({ label, hint, help, error, optional, children, className }: FieldProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(" ") || undefined;
  return (
    <div className={cx("field", className)}>
      <div className="field__label-row">
        <label className="field__label" htmlFor={id}>
          {label}
          {optional ? <span className="optional">{copy.Form.optional}</span> : null}
        </label>
        {help ? <HelpPopover label={typeof label === "string" ? formatCopy(copy.Form.helpFor, { label }) : copy.Form.fieldHelp}>{help}</HelpPopover> : null}
      </div>
      {children({ id, "aria-invalid": error ? true : undefined, "aria-describedby": describedBy })}
      {hint ? (
        <span id={hintId} className="field__hint">
          {hint}
        </span>
      ) : null}
      {error ? (
        <span id={errorId} className="field__error" role="alert">
          <CircleAlert width={13} height={13} aria-hidden />
          {error}
        </span>
      ) : null}
    </div>
  );
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  mono?: boolean;
}
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input({ className, mono, ...rest }, ref) {
  const input = <input ref={ref} className={cx("input", mono && "input--mono", className)} {...rest} />;
  return rest.type === "search" ? <span className="search-input"><Search aria-hidden className="search-input__icon" />{input}</span> : input;
});

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(function Select(
  { className, children, ...rest },
  ref,
) {
  return (
    <select ref={ref} className={cx("select", className)} {...rest}>
      {children}
    </select>
  );
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, ...rest }, ref) {
    return <textarea ref={ref} className={cx("textarea", className)} {...rest} />;
  },
);

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  label: ReactNode;
}
export function Checkbox({ label, className, ...rest }: CheckboxProps) {
  return (
    <label className={cx("check", className)}>
      <input type="checkbox" {...rest} />
      {label}
    </label>
  );
}
